import { InputHTMLAttributes, useId } from 'react';
import { useFormContext, type FieldError as FieldErrorType } from 'react-hook-form';
import formStyles from '~/styles/form.module.css';
import useDialOptions from '~/hooks/useDialOptions';
import phoneNumberFieldStyles from '~/styles/phone-number.module.css';
import cnh from '~/helpers/cnh';
import getWithDot from '~/helpers/getWithDot';
import FormLabel from './FormLabel';
import FieldError from './FieldError';

type Props = {
  label: string;
  placeholder?: string;
  required?: boolean;
  autoComplete?: InputHTMLAttributes<HTMLInputElement>['autoComplete'];
  visuallyHiddenLabel?: boolean;
  numberField: string;
  countryCodeField: string;
};

export default function PhoneNumberField({
  label,
  numberField,
  countryCodeField,
  placeholder,
  required = false,
  visuallyHiddenLabel = false,
}: Props) {
  const fieldId = useId();
  const { options: dialOptions } = useDialOptions();
  const { register, formState: { errors } } = useFormContext();

  const errorForNumberField = getWithDot<FieldErrorType>(numberField, errors);
  const errorForCountryCodeField = getWithDot<FieldErrorType>(countryCodeField, errors);

  return (
    <div>
      <FormLabel
        htmlFor={fieldId}
        required={required}
        visuallyHidden={visuallyHiddenLabel}
      >
        {label}
      </FormLabel>

      <div className={cnh(formStyles.formControlWrapper, phoneNumberFieldStyles.phoneNumberField)}>
        <select
          className={cnh(
            formStyles.formControl,
            formStyles.formSelect,
            phoneNumberFieldStyles.phoneNumberFieldInput,
          )}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...register(countryCodeField, { required })}
        >
          {dialOptions.map((option) => (
            <option key={`${option.value}-${option.label}`} value={option.value}>
              {option.label}
              {` (${option.value.replace(/^00/, '+')})`}
            </option>
          ))}
        </select>

        <input
          type="tel"
          id={fieldId}
          className={formStyles.formControl}
          placeholder={placeholder}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...register(numberField, {
            required,
            minLength: 6,
            maxLength: 20,
          })}
        />
      </div>

      {errorForNumberField && (
        <FieldError error={errorForNumberField} />
      )}

      {errorForCountryCodeField && (
        <FieldError error={errorForCountryCodeField} />
      )}
    </div>
  );
}
